/**
 * Alshallal Pastry Company HR System - AI Chat Panel Component
 * Phase 23.2: Fixed UI with responsive behavior
 * Upgrade (2026-10): streaming answers, tool activity, markdown, role-aware quick actions, feedback.
 *
 * Features:
 * - Employee isolated chat / HR full access (enforced by the backend)
 * - Streams the answer token by token (SSE over fetch), falls back to the JSON endpoint
 * - Multi-language support (RTL)
 * - Draft request creation with confirmation
 * - Clear disclaimer about HR review
 *
 * UI requirements kept from Phase 23.2:
 * - Close "X" always visible, ESC closes, backdrop click closes, panel never off-screen,
 *   internal scroll, docked right panel on desktop, near-full-screen on mobile, RTL support.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import {
  MessageSquare,
  Send,
  Loader2,
  Bot,
  User,
  AlertCircle,
  CheckCircle2,
  X,
  Maximize2,
  Minimize2,
  Info,
  Square,
  ThumbsUp,
  ThumbsDown,
  RotateCcw
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Human labels for tool activity while the assistant works
const TOOL_LABELS = {
  get_my_access: ['Checking permissions', 'جارٍ التحقق من الصلاحيات'],
  get_employee_profile: ['Reading profile', 'جارٍ قراءة الملف الشخصي'],
  search_employees: ['Searching employees', 'جارٍ البحث عن الموظفين'],
  get_leave_balance: ['Checking leave status', 'جارٍ التحقق من حالة الإجازة'],
  list_leave_types: ['Loading leave types', 'جارٍ تحميل أنواع الإجازات'],
  list_leave_requests: ['Fetching leave requests', 'جارٍ جلب طلبات الإجازة'],
  create_leave_request: ['Preparing leave request', 'جارٍ تجهيز طلب الإجازة'],
  list_request_types: ['Loading request types', 'جارٍ تحميل أنواع الطلبات'],
  list_requests: ['Fetching requests', 'جارٍ جلب الطلبات'],
  create_request: ['Preparing request', 'جارٍ تجهيز الطلب'],
  list_letter_types: ['Loading letter types', 'جارٍ تحميل أنواع الخطابات'],
  list_letter_requests: ['Fetching letter requests', 'جارٍ جلب طلبات الخطابات'],
  request_letter: ['Preparing letter request', 'جارٍ تجهيز طلب الخطاب'],
  list_issued_letters: ['Fetching issued letters', 'جارٍ جلب الخطابات الصادرة'],
  approve_letter_request: ['Approving letter request', 'جارٍ اعتماد طلب الخطاب'],
  issue_letter: ['Issuing letter', 'جارٍ إصدار الخطاب'],
  list_documents: ['Fetching documents', 'جارٍ جلب المستندات'],
  list_expiring_documents: ['Checking expiring documents', 'جارٍ فحص المستندات المنتهية'],
  list_pending_approvals: ['Loading pending approvals', 'جارٍ تحميل الطلبات المعلقة'],
  employees_on_leave: ['Checking who is on leave', 'جارٍ التحقق من الموظفين في إجازة'],
  get_hr_overview: ['Building overview', 'جارٍ إعداد الملخص'],
  search_knowledge: ['Searching policies and guides', 'جارٍ البحث في السياسات والأدلة'],
  get_system_updates: ['Loading system updates', 'جارٍ تحميل تحديثات النظام'],
  add_knowledge: ['Saving to knowledge base', 'جارٍ الحفظ في قاعدة المعرفة']
};

const QUICK_ACTIONS = {
  self: [
    { en: 'What is my leave status?', ar: 'ما هي حالة إجازتي؟' },
    { en: 'I need annual leave', ar: 'أحتاج إجازة سنوية' },
    { en: 'I need a salary certificate', ar: 'أحتاج شهادة تعريف بالراتب' },
    { en: 'Which of my documents are expiring?', ar: 'ما هي مستنداتي التي ستنتهي قريباً؟' }
  ],
  hr: [
    { en: 'What is pending my action?', ar: 'ما الطلبات المعلقة لدي؟' },
    { en: 'Who is on leave today?', ar: 'من في إجازة اليوم؟' },
    { en: 'Documents expiring in 30 days', ar: 'المستندات التي تنتهي خلال 30 يوماً' },
    { en: 'Headcount by department', ar: 'عدد الموظفين حسب القسم' },
    { en: 'Any system issues?', ar: 'هل توجد مشاكل في النظام؟' }
  ]
};

// ---- tiny, safe markdown renderer (builds React nodes, never raw HTML) ----
const renderInline = (text, keyPrefix) => {
  const parts = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) parts.push(<strong key={`${keyPrefix}-b${i++}`}>{tok.slice(2, -2)}</strong>);
    else parts.push(<code key={`${keyPrefix}-c${i++}`} className="px-1 rounded bg-slate-200/70 text-[0.85em]">{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

const SimpleMarkdown = ({ text, isRTL }) => {
  const lines = (text || '').split('\n');
  const blocks = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    // table: header + separator
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      const header = line.split('|').slice(1, -1).map(s => s.trim());
      const rows = [];
      i += 2;
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(lines[i].split('|').slice(1, -1).map(s => s.trim()));
        i++;
      }
      blocks.push(
        <div key={`t${k++}`} className="overflow-x-auto my-2">
          <table className="text-xs border-collapse min-w-full">
            <thead><tr>{header.map((h, hi) => <th key={hi} className="border border-slate-300 bg-slate-200/60 px-2 py-1 text-start font-semibold">{renderInline(h, `h${hi}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} className="border border-slate-300 px-2 py-1 align-top">{renderInline(c, `c${ri}-${ci}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^\s*([-*•]|\d+[.)])\s+/.test(line)) {
      const ordered = /^\s*\d+[.)]\s+/.test(line);
      const items = [];
      while (i < lines.length && /^\s*([-*•]|\d+[.)])\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*([-*•]|\d+[.)])\s+/, ''));
        i++;
      }
      const Tag = ordered ? 'ol' : 'ul';
      blocks.push(
        <Tag key={`l${k++}`} className={cn('my-1 space-y-0.5', ordered ? 'list-decimal' : 'list-disc', isRTL ? 'pr-5' : 'pl-5')}>
          {items.map((it, ii) => <li key={ii}>{renderInline(it, `li${ii}`)}</li>)}
        </Tag>
      );
      continue;
    }
    if (/^\s*#{1,4}\s+/.test(line)) {
      blocks.push(<p key={`hd${k++}`} className="font-semibold mt-2 mb-1">{renderInline(line.replace(/^\s*#{1,4}\s+/, ''), `hd${k}`)}</p>);
      i++;
      continue;
    }
    if (line.trim() === '') {
      i++;
      continue;
    }
    // paragraph: merge consecutive plain lines
    const para = [line];
    i++;
    while (i < lines.length && lines[i].trim() !== '' && !/^\s*([-*•]|\d+[.)]|#{1,4}|\|)\s*/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(<p key={`p${k++}`} className="my-1 whitespace-pre-wrap">{renderInline(para.join('\n'), `p${k}`)}</p>);
  }
  return <div className="text-sm leading-relaxed">{blocks}</div>;
};

// Message bubble component
const MessageBubble = ({ message, isUser, isRTL, createdRequestId, onFeedback }) => (
  <div className={cn(
    'flex gap-2 mb-4',
    isUser ? (isRTL ? 'flex-row' : 'flex-row-reverse') : 'flex-row'
  )}>
    <div className={cn(
      'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
      isUser ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
    )}>
      {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
    </div>
    <div className={cn('flex-1 max-w-[85%]', isRTL ? 'text-right' : 'text-left')} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={cn('rounded-lg px-4 py-2', isUser ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-900')}>
        {isUser
          ? <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
          : <SimpleMarkdown text={message.content} isRTL={isRTL} />}
        {!isUser && message.streaming && (
          <span className="inline-block w-2 h-4 align-middle bg-slate-400 animate-pulse ml-1" aria-hidden="true" />
        )}
        {createdRequestId && (
          <div className="mt-2 flex items-center gap-2 text-xs">
            <CheckCircle2 className="h-3 w-3" />
            <span>{isRTL ? 'تم إنشاء طلب للمراجعة' : 'Request created for HR review'}</span>
          </div>
        )}
      </div>
      <div className={cn('flex items-center gap-2 mt-1', isUser ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end flex-row-reverse' : 'justify-start'))}>
        <p className="text-xs text-slate-400">
          {message.timestamp ? format(new Date(message.timestamp), 'HH:mm') : ''}
        </p>
        {!isUser && !message.streaming && message.canRate && (
          <span className="flex items-center gap-1">
            <button type="button" aria-label="Helpful" onClick={() => onFeedback(message, true)}
              className={cn('p-0.5 rounded hover:bg-slate-200', message.feedback === true && 'text-green-600')}>
              <ThumbsUp className="h-3 w-3" />
            </button>
            <button type="button" aria-label="Not helpful" onClick={() => onFeedback(message, false)}
              className={cn('p-0.5 rounded hover:bg-slate-200', message.feedback === false && 'text-red-600')}>
              <ThumbsDown className="h-3 w-3" />
            </button>
          </span>
        )}
      </div>
    </div>
  </div>
);

const AIChatPanel = ({
  isFloating = false,
  isOpen = true,
  onToggle,
  className
}) => {
  const { token } = useAuth();
  const { isRTL } = useLanguage();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activity, setActivity] = useState(null);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [aiEnabled, setAiEnabled] = useState(null);
  const [scope, setScope] = useState('self');
  const [isExpanded, setIsExpanded] = useState(false);
  const [quotaRemaining, setQuotaRemaining] = useState(null);
  const [ticketNumber, setTicketNumber] = useState(null);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const abortRef = useRef(null);

  const handleClose = useCallback(() => {
    if (onToggle) onToggle();
  }, [onToggle]);

  useEffect(() => {
    if (!isFloating || !isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFloating, isOpen, handleClose]);

  // Check AI status on mount
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/ai/status`, { headers: { Authorization: `Bearer ${token}` } });
        setAiEnabled(response.data.enabled);
        setQuotaRemaining(response.data.quota?.remaining);
        setScope(response.data.scope || (response.data.features?.full_system_access ? 'hr' : 'self'));
      } catch (err) {
        setAiEnabled(false);
      }
    };
    checkStatus();
  }, [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activity]);

  useEffect(() => () => { abortRef.current?.abort(); }, []);

  const toolLabel = (name) => {
    const pair = TOOL_LABELS[name];
    if (!pair) return isRTL ? 'جارٍ المعالجة…' : 'Working…';
    return (isRTL ? pair[1] : pair[0]) + '…';
  };

  const updateAssistant = (id, patch) => {
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m)));
  };

  // Stream via SSE-over-fetch. Resolves true when handled, false to fall back.
  const streamChat = async (content, assistantId, signal) => {
    const response = await fetch(`${API_URL}/ai/chat/stream`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify({ message: content, conversation_id: conversationId }),
      signal
    });
    if (response.status === 404 || response.status === 405) return false; // older backend
    if (!response.ok) {
      let detail = `HTTP ${response.status}`;
      try {
        const data = await response.json();
        detail = data?.detail?.error || data?.detail || data?.error || detail;
      } catch (_) { /* ignore */ }
      throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let gotFinal = false;
    const handle = (evt) => {
      if (evt.type === 'text') {
        setActivity(null);
        updateAssistant(assistantId, m => ({ content: (m.content || '') + evt.text }));
      } else if (evt.type === 'tool') {
        setActivity(evt.status === 'start' ? toolLabel(evt.name) : null);
      } else if (evt.type === 'final') {
        gotFinal = true;
        setConversationId(evt.conversation_id);
        if (evt.quota_remaining !== undefined && evt.quota_remaining !== null) setQuotaRemaining(evt.quota_remaining);
        if (evt.ticket_number) setTicketNumber(evt.ticket_number);
        updateAssistant(assistantId, m => ({
          content: evt.text || m.content,
          streaming: false,
          canRate: true,
          conversationId: evt.conversation_id,
          created_request_id: evt.created_request_id || null
        }));
      } else if (evt.type === 'error') {
        gotFinal = true;
        setError(evt.message || 'Failed to send message');
        updateAssistant(assistantId, m => ({ streaming: false, content: m.content || (isRTL ? 'تعذر إكمال الإجابة.' : 'The answer could not be completed.') }));
      }
    };
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buffer.indexOf('\n\n')) >= 0) {
        const frame = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        const dataLine = frame.split('\n').find(l => l.startsWith('data:'));
        if (!dataLine) continue;
        try { handle(JSON.parse(dataLine.slice(5).trim())); } catch (_) { /* skip malformed frame */ }
      }
    }
    if (!gotFinal) updateAssistant(assistantId, { streaming: false, canRate: true });
    return true;
  };

  const jsonChat = async (content, assistantId) => {
    const response = await axios.post(`${API_URL}/ai/chat`, { message: content, conversation_id: conversationId },
      { headers: { Authorization: `Bearer ${token}` } });
    setConversationId(response.data.conversation_id);
    setQuotaRemaining(response.data.quota_remaining);
    if (response.data.ticket_number) setTicketNumber(response.data.ticket_number);
    updateAssistant(assistantId, {
      content: response.data.reply, streaming: false, canRate: true,
      conversationId: response.data.conversation_id, created_request_id: response.data.created_request_id || null
    });
  };

  const handleSend = async (preset) => {
    const content = (preset ?? input).trim();
    if (!content || isLoading) return;
    const userMessage = { id: Date.now(), role: 'user', content, timestamp: new Date().toISOString() };
    const assistantId = Date.now() + 1;
    const assistantMessage = { id: assistantId, role: 'assistant', content: '', streaming: true, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage, assistantMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);
    setTicketNumber(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const handled = await streamChat(content, assistantId, controller.signal);
      if (!handled) await jsonChat(content, assistantId);
    } catch (err) {
      if (err.name === 'AbortError') {
        updateAssistant(assistantId, m => ({ streaming: false, content: m.content || (isRTL ? 'تم الإيقاف.' : 'Stopped.') }));
      } else {
        const detail = err.response?.data?.detail;
        setError((detail && (detail.error || detail)) || err.message || 'Failed to send message');
        updateAssistant(assistantId, m => ({ streaming: false, content: m.content || (isRTL ? 'تعذر إرسال الرسالة.' : 'The message could not be sent.') }));
      }
    } finally {
      setIsLoading(false);
      setActivity(null);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  };

  const handleStop = () => abortRef.current?.abort();

  const handleFeedback = async (message, helpful) => {
    const cid = message.conversationId || conversationId;
    if (!cid) return;
    updateAssistant(message.id, { feedback: helpful });
    try {
      await axios.post(`${API_URL}/ai/learning/feedback`, null, {
        params: { conversation_id: cid, was_helpful: helpful },
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (_) { /* non-blocking */ }
  };

  const handleNewConversation = () => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationId(null);
    setError(null);
    setTicketNumber(null);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  if (aiEnabled === false) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="p-8 text-center">
          <Bot className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h3 className="font-medium text-slate-600 mb-2">
            {isRTL ? 'المساعد الذكي غير متاح' : 'AI Assistant Not Available'}
          </h3>
          <p className="text-sm text-slate-500">
            {isRTL ? 'يرجى التواصل مع الموارد البشرية للمساعدة' : 'Please contact HR for assistance'}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (aiEnabled === null) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-500" />
        </CardContent>
      </Card>
    );
  }

  if (!isFloating) {
    return (
      <Card className={cn('flex flex-col w-full h-full', className)}>
        {renderChatContent()}
      </Card>
    );
  }

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 z-40 md:bg-transparent md:pointer-events-none"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={cn(
          'fixed z-50 flex flex-col bg-white shadow-2xl',
          'inset-2 rounded-lg',
          'sm:inset-auto sm:bottom-4 sm:top-auto sm:h-auto',
          isRTL ? 'sm:left-4 sm:right-auto' : 'sm:right-4 sm:left-auto',
          'sm:w-[400px] sm:max-w-[calc(100vw-2rem)]',
          'sm:max-h-[calc(100vh-2rem)]',
          isExpanded && 'sm:w-[640px] sm:h-[680px]',
          !isExpanded && 'sm:h-[540px]'
        )}
        role="dialog"
        aria-modal="true"
        aria-label={isRTL ? 'مساعد الموارد البشرية' : 'HR Assistant'}
      >
        <Card className="flex flex-col h-full overflow-hidden rounded-lg border-0">
          {renderChatContent()}
        </Card>
      </div>
    </>
  );

  function renderChatContent() {
    const quick = QUICK_ACTIONS[scope === 'hr' ? 'hr' : 'self'];
    return (
      <>
        <CardHeader className="pb-2 border-b bg-gradient-to-r from-purple-50 to-blue-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <Bot className="h-4 w-4 text-purple-700" />
              </div>
              <div>
                <CardTitle className="text-base">
                  {isRTL ? 'مساعد الموارد البشرية' : 'HR Assistant'}
                </CardTitle>
                <p className="text-xs text-slate-500">
                  {scope === 'hr'
                    ? (isRTL ? 'وضع الموارد البشرية — وصول كامل' : 'HR mode — full access')
                    : (isRTL ? 'بياناتك الخاصة فقط' : 'Your own information only')}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100" onClick={handleNewConversation}
                  title={isRTL ? 'محادثة جديدة' : 'New conversation'} aria-label="New conversation">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
              {isFloating && (
                <>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100"
                    onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? 'Minimize' : 'Expand'}>
                    {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-red-100 hover:text-red-600"
                    onClick={handleClose} title={isRTL ? 'إغلاق' : 'Close'} aria-label="Close chat">
                    <X className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>

        <div className="bg-amber-50 border-b border-amber-100 px-4 py-2 flex items-center justify-between text-xs text-amber-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Info className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">
              {isRTL ? 'يتم تأكيد كل طلب قبل إرساله — القرار للموارد البشرية' : 'Every submission is confirmed first — HR makes final decisions'}
            </span>
          </div>
          {quotaRemaining !== null && quotaRemaining !== undefined && (
            <Badge variant="outline" className={cn('text-xs flex-shrink-0 ml-2', quotaRemaining < 0 && 'bg-green-50 text-green-700 border-green-200')}>
              {quotaRemaining < 0 ? (isRTL ? 'غير محدود' : 'Unlimited') : (isRTL ? `${quotaRemaining} متبقي` : `${quotaRemaining} left`)}
            </Badge>
          )}
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <MessageSquare className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-600 mb-2">
                  {isRTL ? 'ابدأ محادثة' : 'Start a conversation'}
                </h3>
                <p className="text-sm text-slate-500 max-w-xs mx-auto">
                  {scope === 'hr'
                    ? (isRTL ? 'اسأل عن أي موظف أو الطلبات المعلقة أو السياسات، أو اطلب إصدار خطاب' : 'Ask about any employee, pending work, policies, or issue a letter')
                    : (isRTL ? 'اسأل عن إجازتك أو مستنداتك أو السياسات، أو قدم طلباً' : 'Ask about your leave, documents or policies, or submit a request')}
                </p>
                <div className="mt-5 flex flex-wrap gap-2 justify-center">
                  {quick.map((q, i) => (
                    <Button key={i} variant="outline" size="sm" className="text-xs" onClick={() => handleSend(isRTL ? q.ar : q.en)}>
                      {isRTL ? q.ar : q.en}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} isUser={msg.role === 'user'} isRTL={isRTL}
                createdRequestId={msg.created_request_id} onFeedback={handleFeedback} />
            ))}

            {isLoading && (
              <div className="flex items-center gap-2 text-slate-500" dir={isRTL ? 'rtl' : 'ltr'}>
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{activity || (isRTL ? 'جاري الكتابة...' : 'Typing...')}</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {error && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-100 flex items-center gap-2 text-sm text-red-600 flex-shrink-0">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1 truncate">{typeof error === 'string' ? error : JSON.stringify(error)}</span>
            <Button variant="ghost" size="sm" className="h-6 px-2 flex-shrink-0" onClick={() => setError(null)}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {ticketNumber && (
          <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex items-center gap-2 text-sm text-blue-700 flex-shrink-0">
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
            <span>{isRTL ? `تم إنشاء تذكرة: ${ticketNumber}` : `Ticket created: ${ticketNumber}`}</span>
          </div>
        )}

        <div className="p-4 border-t bg-slate-50 flex-shrink-0">
          <div className="flex gap-2">
            <Textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              placeholder={isRTL ? 'اكتب رسالتك...' : 'Type your message...'}
              className="min-h-[40px] max-h-[100px] resize-none flex-1"
              dir={isRTL ? 'rtl' : 'ltr'}
              disabled={isLoading}
            />
            {isLoading ? (
              <Button onClick={handleStop} variant="outline" className="self-end flex-shrink-0" title={isRTL ? 'إيقاف' : 'Stop'} aria-label="Stop">
                <Square className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={() => handleSend()} disabled={!input.trim()} className="self-end flex-shrink-0" aria-label="Send">
                <Send className={cn('h-4 w-4', isRTL && 'rotate-180')} />
              </Button>
            )}
          </div>
        </div>
      </>
    );
  }
};

export default AIChatPanel;
